import { Injectable, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, map, Observable, startWith } from 'rxjs';
import { PARAFFIN_CONSTANTS } from '../common/constants';
import { commonConstants, reportPopupConstants, viewNotesConstants } from '../common/experiment-constants';
import { SharedService } from './shared.service';

export interface HeaderState {
  username: string | undefined;
  modifiedRoleName: string;
  projectId: string | null;
  projectNumber: string | null;
  projectIdQueryParam: string | null;
  projectNumberQueryParam: string | null;
  experimentName: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class HeaderService {
  private sharedService = inject(SharedService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  GetHeaderState(): Observable<HeaderState> {
    const userState$ = this.sharedService.userDetail$.pipe(
      map((userDetail) => ({
        username: userDetail?.displayName,
        modifiedRoleName: userDetail?.roleName ?? commonConstants.emptyString,
      })),
    );

    const queryState$ = this.route.queryParamMap.pipe(
      map((param) => ({
        projectIdQueryParam: param.get(PARAFFIN_CONSTANTS.projectId),
        projectNumberQueryParam: param.get(PARAFFIN_CONSTANTS.projectNumber),
      })),
    );

    const routeParamsState$ = this.router.events.pipe(
      startWith(null),
      map(() => this.GetActiveRouteParams()),
    );

    return combineLatest([userState$, queryState$, routeParamsState$]).pipe(
      map(([userState, queryState, routeState]) => ({
        username: userState.username,
        modifiedRoleName: userState.modifiedRoleName,
        projectId: routeState.projectId,
        projectNumber: routeState.projectNumber,
        projectIdQueryParam: queryState.projectIdQueryParam,
        projectNumberQueryParam: queryState.projectNumberQueryParam,
        experimentName: routeState.experimentName,
      })),
    );
  }

  GetBackToSampleList(projectIdQueryParam: string | null, projectNumberQueryParam: string | null): void {
    this.sharedService.NavigateToLabList(projectIdQueryParam ?? commonConstants.emptyString, projectNumberQueryParam ?? commonConstants.emptyString);
  }

  GoBackToProjectDetails(projectId: string | null): void {
    this.sharedService.NavigateToProjectDetail(Number(projectId));
  }

  private GetRoleName(roleName: string): string {
    if (roleName.includes(viewNotesConstants.fluid) && roleName.includes(viewNotesConstants.engineers)) {
      return viewNotesConstants.fluidEnginner;
    }

    if (roleName.includes(viewNotesConstants.lab) && roleName.includes(viewNotesConstants.technicians)) {
      return viewNotesConstants.labTechnician;
    }

    if (roleName.includes(viewNotesConstants.technical) && roleName.includes(viewNotesConstants.resources)) {
      return viewNotesConstants.technicalResources;
    }

    return commonConstants.emptyString;
  }

  private GetActiveRouteParams(): { experimentName: string | null; projectNumber: string | null; projectId: string | null } {
    let activeRoute = this.route.root;

    while (activeRoute.firstChild) {
      activeRoute = activeRoute.firstChild;
    }

    const params = activeRoute.snapshot.paramMap;

    return {
      experimentName: params.get(reportPopupConstants.experimentName),
      projectNumber: params.get(PARAFFIN_CONSTANTS.projectNumber),
      projectId: params.get(PARAFFIN_CONSTANTS.projectId),
    };
  }
}